import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import useChart from './useChart';

// ---------------------------------------------------------------------------
// One time-series chart, any number of quantities on it.
//
// Every chart in the app so far has been written for one or two fixed series,
// because two is where a hand-rolled `yaxis: [{...}, {opposite: true, ...}]`
// stops being readable. That ceiling is the reason the recordings page made you
// pick a "primary" and a "secondary" sensor, and the reason the sensor manager
// has five separate charts. Here the axes are derived from the series, so N
// quantities on unrelated scales — ppm, an index, °C, µg/m³ — can share one
// time axis without any of them being flattened.
//
// Each series carries its own axis, and that axis is coloured and named after
// the series, which is what makes the chart readable: a reader traces a
// coloured line to the axis of the same colour rather than counting legend
// entries.
// ---------------------------------------------------------------------------

// Distinct in hue rather than a gradient, so adjacent series never read as the
// same colour, and each one legible against both the light and dark paper.
export const SERIES_PALETTE = [
  '#1976D2', // blue
  '#E8833A', // orange
  '#2E9E6B', // green
  '#B4477E', // magenta
  '#8E7CC3', // violet
  '#C4442F', // red
  '#3AA6B9', // teal
  '#9A7B2F', // ochre
];

const isFinitePoint = (v) => typeof v === 'number' && Number.isFinite(v);

const toMillis = (t) => {
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'number') return t;
  const parsed = new Date(t).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

// A series is `{ id, name, unit, color, data }`, where data is a list of
// `[time, value]` pairs — time as epoch ms, a Date or an ISO string.
//
// Pairs, not bare values against a shared category list: two quantities
// recorded by different devices do not share a sample clock, and the old
// value-list charts dealt with that by slicing both series to the shorter one.
// With `[t, v]` each trace carries its own clock and nothing is discarded.
export function normalizeSeries(series = []) {
  const seen = new Map();
  return series.map((s, i) => {
    // Apex binds a y axis to its series *by name*. Two series sharing a display
    // name would put both traces on the first axis and leave the second empty,
    // so a repeat gets a disambiguating suffix.
    const base = s.name ?? `Series ${i + 1}`;
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    const name = count === 1 ? base : `${base} (${count})`;

    const data = (s.data || [])
      .map(([t, v]) => [toMillis(t), v])
      .filter(([t, v]) => t !== null && isFinitePoint(v));

    return {
      id: s.id ?? name,
      name,
      unit: s.unit || null,
      color: s.color || SERIES_PALETTE[i % SERIES_PALETTE.length],
      data,
    };
  });
}

const axisTitle = (s) => (s.unit ? `${s.name} (${s.unit})` : s.name);

// Apex pads every tick to the same decimal count, which on a chart carrying
// several axes spends a lot of narrow horizontal space on "900.00". Show the
// precision the number actually has, capped at two places.
export const axisLabel = (v) => {
  if (!isFinitePoint(v)) return v;
  return String(Number(v.toFixed(2)));
};

export default function MultiSeriesTimeChart({
  series,
  height = 320,
  annotations = undefined,
  onPointClick = undefined,
  emptyMessage = 'Nothing to chart',
  toolbar = true,
}) {
  const normalized = normalizeSeries(series);
  const hasData = normalized.some((s) => s.data.length > 0);

  const options = useChart({
    colors: normalized.map((s) => s.color),
    // The whole point of the component: one axis per quantity, wearing its
    // series' colour and name so it can be read back to its trace.
    yaxis: normalized.map((s, i) => ({
      seriesName: s.name,
      // Alternate sides so a handful of axes frame the plot instead of
      // stacking up on the left of it.
      opposite: i % 2 === 1,
      // Without this an axis disappears when its series is toggled off in the
      // legend, and every axis to its right shifts colour-to-trace by one.
      showAlways: true,
      title: { text: axisTitle(s), style: { color: s.color } },
      labels: { style: { colors: s.color }, formatter: axisLabel },
      axisBorder: { show: true, color: s.color },
      axisTicks: { show: true, color: s.color },
    })),
    xaxis: {
      type: 'datetime',
      // Timestamps are recorded in UTC; readers think in their own clock.
      labels: { datetimeUTC: false },
    },
    stroke: { width: 2, curve: 'smooth' },
    // No legend: each series already carries its name on its own axis, in its
    // own colour, and the caller's selector shows the same colours. A legend
    // would be a third copy of the key, spending a row of the plot on it.
    legend: { show: false },
    tooltip: {
      shared: true,
      x: { format: 'HH:mm:ss' },
      // Per-series, so a shared tooltip names each quantity's own unit rather
      // than borrowing the first series'.
      y: normalized.map((s) => ({
        formatter: (v) => (isFinitePoint(v) ? `${v}${s.unit ? ` ${s.unit}` : ''}` : '—'),
      })),
    },
    chart: {
      // Only the download menu. The drag-driven tools (zoom, pan, selection)
      // all run through ApexCharts' ZoomPanSelection, which captures
      // `.apexcharts-grid` once at init and dereferences it on every mousedown
      // — and that element does not exist yet when the chart mounts inside a
      // container that is not displayed, so the first click on the plot throws
      // "Cannot read properties of null (reading 'getBoundingClientRect')".
      // Nothing else in this app enables zoom; click-to-seek needs the plot to
      // stay clickable more than the chart needs drag-zoom.
      toolbar: toolbar ? { show: true, tools: { download: true } } : { show: false },
      zoom: { enabled: false },
      events: {
        markerClick: (_, __, opts = {}) => {
          if (!onPointClick) return;
          const point = normalized[opts.seriesIndex]?.data[opts.dataPointIndex];
          if (point) onPointClick(point[0]);
        },
      },
    },
    ...(annotations ? { annotations } : {}),
  });

  // useChart merges onto a default palette, and a lodash merge of two arrays
  // keeps the longer one's tail — leaving colours the chart never uses on the
  // end of the list. Replace it outright so `colors[i]` is series i's colour
  // and nothing else.
  options.colors = normalized.map((s) => s.color);

  if (!hasData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: height / 2 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box dir="ltr">
      <ReactApexChart type="line" series={normalized} options={options} height={height} />
    </Box>
  );
}

MultiSeriesTimeChart.propTypes = {
  series: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      unit: PropTypes.string,
      color: PropTypes.string,
      data: PropTypes.array,
    })
  ).isRequired,
  height: PropTypes.number,
  annotations: PropTypes.object,
  onPointClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  toolbar: PropTypes.bool,
};
